import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Breadcrumbs,
  Link,
  Divider,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Tabs,
  Tab,
  IconButton,
  useTheme,
  useMediaQuery,
  alpha,
  Fade,
  Slide,
  Zoom,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Home as HomeIcon,
  Store as StoreIcon,
  ArrowBack as ArrowBackIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Share as ShareIcon,
  Star as StarIcon,
  RecordVoiceOver as SuggestionIcon
} from '@mui/icons-material';
import { InventoryItem } from '../../utils/types';
import { getProductById, getProductReviews } from '../../services/storeService';
import { useCart } from '../../contexts/CartContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { CartDrawer } from '../../components/store';
import ProductImageGallery from '../../components/store/ProductImageGallery';
// استخدام مسار نسبي للصورة الافتراضية
const placeholderImage = '/assets/images/product-placeholder.png';
import { Review } from '../../types/store';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';
import ProductSuggestionModal from '../../components/store/ProductSuggestionModal';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const MobileOptimizedProductDetailPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { formatCurrency } = useCurrency();

  const [product, setProduct] = useState<InventoryItem | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [cartOpen, setCartOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [suggestionModalOpen, setSuggestionModalOpen] = useState(false);
  const { cart, addToCart, isInCart } = useCart();
  const { settings: storeSettings } = useStoreSettings();

  // التمرير إلى أعلى الصفحة عند تغيير المنتج
  useScrollToTop([productId]);

  useEffect(() => {

    const fetchProductDetails = async () => {
      if (!productId) {
        setError('معرف المنتج غير صالح');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('Fetching product details for ID:', productId);

        // جلب تفاصيل المنتج
        const productData = await getProductById(productId);
        console.log('Product data received:', productData);
        setProduct(productData);

        // جلب المراجعات
        try {
          const reviewsData = await getProductReviews(productId);
          setReviews(reviewsData);
        } catch (reviewErr) {
          console.error('Error fetching product reviews:', reviewErr);
          setReviews([]);
        }

        setError(null);
      } catch (err: any) {
        console.error('Error fetching product details:', err);
        const errorMessage = err?.message || 'حدث خطأ أثناء تحميل تفاصيل المنتج';
        setError(errorMessage === 'Product not found' ? 'لم يتم العثور على المنتج' : 'حدث خطأ أثناء تحميل تفاصيل المنتج');
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [productId]);

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && product && newQuantity <= product.quantity) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, quantity);
      setCartOpen(true);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFavoriteToggle = () => {
    setIsFavorite(!isFavorite);
  };

  const handleShare = () => {
    if (navigator.share && product) {
      navigator.share({
        title: product.name,
        text: product.description || 'منتج رائع من متجرنا',
        url: window.location.href
      });
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'لم يتم العثور على المنتج'}
        </Alert>
        <Button
          component={RouterLink}
          to="/mobile-store"
          variant="contained"
        >
          العودة إلى المتجر
        </Button>
      </Container>
    );
  }

  const isOutOfStock = product.quantity <= 0;
  const isLowStock = product.quantity <= 5 && product.quantity > 0;

  return (
    <>
      {/* شريط التنقل العلوي للهاتف */}
      {isMobile && (
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(10px)',
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            color: theme.palette.text.primary
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', minHeight: '56px !important' }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleGoBack}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
              }}
            >
              <ArrowBackIcon />
            </IconButton>

            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 'bold',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '60%'
              }}
            >
              {product.name}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                color="inherit"
                onClick={handleFavoriteToggle}
                sx={{
                  color: isFavorite ? theme.palette.error.main : theme.palette.grey[600],
                  bgcolor: alpha(theme.palette.background.paper, 0.8),
                  '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) }
                }}
              >
                {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
              </IconButton>

              <IconButton
                color="inherit"
                onClick={handleShare}
                sx={{
                  bgcolor: alpha(theme.palette.background.paper, 0.8),
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) }
                }}
              >
                <ShareIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <Container
        maxWidth="lg"
        sx={{
          mt: isMobile ? 0 : 4,
          mb: isMobile ? 10 : 8,
          px: isMobile ? 1 : 3
        }}
      >
        {/* Breadcrumbs - للشاشات الكبيرة فقط */}
        {!isMobile && (
          <Fade in timeout={500}>
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Link
                  component={RouterLink}
                  to="/"
                  color="inherit"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  الرئيسية
                </Link>
                <Link
                  component={RouterLink}
                  to="/mobile-store"
                  color="inherit"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <StoreIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  المتجر
                </Link>
                <Typography color="text.primary">{product.name}</Typography>
              </Breadcrumbs>
            </Box>
          </Fade>
        )}

        {/* صورة المنتج - تظهر أولاً على الهاتف */}
        <Slide in direction="up" timeout={600}>
          <Paper
            sx={{
              p: isMobile ? 2 : 3,
              mb: 3,
              borderRadius: isMobile ? 2 : 3,
              overflow: 'hidden'
            }}
          >
            <ProductImageGallery
              mainImage={product.imageUrl || product.image}
              additionalImages={product.additionalImages}
              productName={product.name}
            />
          </Paper>
        </Slide>

        {/* تفاصيل المنتج */}
        <Fade in timeout={800}>
          <Paper
            sx={{
              p: isMobile ? 2 : 3,
              mb: 3,
              borderRadius: isMobile ? 2 : 3
            }}
          >
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              {product.name}
            </Typography>

            <Typography
              variant={isMobile ? "h6" : "h5"}
              color="primary"
              sx={{ mb: 2, fontWeight: 'bold' }}
            >
              {formatCurrency(product.sellingPrice)}
            </Typography>

            {/* شارات الحالة */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {isOutOfStock ? (
                <Chip label="نفذت الكمية" color="error" />
              ) : isLowStock ? (
                <Chip label={`متبقي ${product.quantity}`} color="warning" />
              ) : (
                <Chip label="متوفر" color="success" />
              )}

              <Chip
                label={product.category}
                variant="outlined"
                sx={{ borderColor: theme.palette.primary.main }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
              {product.description || 'لا يوجد وصف متاح لهذا المنتج.'}
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                رمز المنتج: {product.sku}
              </Typography>
            </Box>

            {/* اختيار الكمية وإضافة للسلة */}
            {!isOutOfStock && (
              <Zoom in timeout={1000}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                    <Typography variant="subtitle1" sx={{ minWidth: 'fit-content' }}>
                      الكمية:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleQuantityChange(quantity - 1)}
                        disabled={quantity <= 1}
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                        }}
                      >
                        <RemoveIcon />
                      </IconButton>
                      <TextField
                        value={quantity}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (!isNaN(value)) {
                            handleQuantityChange(value);
                          }
                        }}
                        inputProps={{
                          min: 1,
                          max: product.quantity,
                          style: { textAlign: 'center' }
                        }}
                        sx={{ width: 80 }}
                        size="small"
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleQuantityChange(quantity + 1)}
                        disabled={quantity >= product.quantity}
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<ShoppingCartIcon />}
                    onClick={handleAddToCart}
                    disabled={isInCart(product.id)}
                    fullWidth
                    sx={{
                      py: isMobile ? 1.5 : 1,
                      fontSize: isMobile ? '1rem' : '1.1rem',
                      fontWeight: 'bold',
                      borderRadius: 2
                    }}
                  >
                    {isInCart(product.id) ? 'تمت الإضافة إلى السلة' : 'أضف إلى السلة'}
                  </Button>

                  {/* زر اقتراح سعر أقل */}
                  {storeSettings?.productSuggestion?.isEnabled && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="large"
                      startIcon={<SuggestionIcon />}
                      onClick={() => setSuggestionModalOpen(true)}
                      fullWidth
                      sx={{
                        mt: 2,
                        py: isMobile ? 1.5 : 1,
                        fontSize: isMobile ? '1rem' : '1.1rem',
                        fontWeight: 'bold',
                        borderRadius: 2
                      }}
                    >
                      {storeSettings.productSuggestion.buttonText}
                    </Button>
                  )}
                </Box>
              </Zoom>
            )}
          </Paper>
        </Fade>

        {/* التفاصيل والمراجعات */}
        <Slide in direction="up" timeout={1200}>
          <Paper sx={{ mb: 4, borderRadius: isMobile ? 2 : 3 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="product tabs"
              centered
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 'bold',
                  fontSize: isMobile ? '0.9rem' : '1rem'
                }
              }}
            >
              <Tab label="التفاصيل" id="product-tab-0" />
              <Tab
                label={`المراجعات (${reviews.length})`}
                id="product-tab-1"
              />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                {product.description || 'لا توجد تفاصيل إضافية لهذا المنتج.'}
              </Typography>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {reviews.length > 0 ? (
                reviews.map((review) => (
                  <Box key={review.id} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {review.customerName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {new Date(review.createdAt).toLocaleDateString('ar-EG')}
                    </Typography>
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      {[...Array(5)].map((_, i) => (
                        <StarIcon
                          key={i}
                          sx={{
                            color: i < review.rating ? '#FFD700' : '#e0e0e0',
                            fontSize: '1.2rem'
                          }}
                        />
                      ))}
                    </Box>
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      {review.comment}
                    </Typography>
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                ))
              ) : (
                <Typography variant="body1" textAlign="center" color="text.secondary">
                  لا توجد مراجعات لهذا المنتج حتى الآن.
                </Typography>
              )}
            </TabPanel>
          </Paper>
        </Slide>
      </Container>

      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
      />

      {/* نافذة اقتراح سعر أقل */}
      {product && storeSettings && storeSettings.productSuggestion && (
        <ProductSuggestionModal
          open={suggestionModalOpen}
          onClose={() => setSuggestionModalOpen(false)}
          product={product}
          settings={storeSettings.productSuggestion}
        />
      )}
    </>
  );
};

export default MobileOptimizedProductDetailPage;
